import React, { useState, useMemo } from "react";
import {
  ChartBarIcon,
  ClockIcon,
  UserGroupIcon,
  TrophyIcon, // Corrected import: Replaced TargetIcon with TrophyIcon for goals/productivity
  MapPinIcon,
  RocketLaunchIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon,
  XCircleIcon, // Added missing import for the Lowest Performer card
} from "@heroicons/react/24/outline";

// --- GLOBAL MOCK DATA ---
const MOCK_ANALYTICS_METRICS = {
    averageProductivity: 78, // % time at verified locations
    averageUtilization: 85, // % of scheduled time active
    topPerformer: "Arjun Singh",
    lowPerformer: "Ravi Menon",
    totalAgents: 152,
};

const MOCK_TIME_ALLOCATION = [
    { category: "Client Site/Work Location", percentage: 55, color: "bg-indigo-500" },
    { category: "Travel Time (Verified)", percentage: 25, color: "bg-teal-500" },
    { category: "Idle Time (Non-Compliant)", percentage: 12, color: "bg-red-500" },
    { category: "Compliance/Admin Tasks", percentage: 8, color: "bg-yellow-500" },
];

const MOCK_PERFORMANCE_TRENDS = [
    { month: "Jan", productivity: 72 },
    { month: "Feb", productivity: 75 },
    { month: "Mar", productivity: 78 },
    { month: "Apr", productivity: 81 },
];

// --- HELPER COMPONENTS ---

// Summary Card Component (Reused/Adapted)
const AnalyticsCard = ({ title, value, icon: Icon, colorClass, footer }) => (
    <div className="bg-white p-5 rounded-xl shadow-2xl border border-gray-100 flex flex-col justify-between h-full transition duration-500 hover:shadow-indigo-300/50 hover:border-indigo-400 transform hover:-translate-y-0.5">
        <div className="flex items-start justify-between">
            <div>
                <p className="text-sm font-poppins font-medium text-gray-500">{title}</p>
                <p className="text-3xl font-poppins font-bold text-gray-900 mt-2">{value}</p>
            </div>
            <div className={`p-3 rounded-xl ${colorClass}/10`}>
                <Icon className={`h-7 w-7 ${colorClass}`} />
            </div>
        </div>
        <p className="text-xs font-roboto mt-4 text-gray-400 border-t pt-2">{footer}</p>
    </div>
);

// Action Button with Loading State (Reused)
const ActionButton = ({ onClick, children, className, loading }) => (
    <button
        onClick={onClick}
        disabled={loading}
        className={`px-6 py-2.5 text-base font-semibold rounded-lg transition duration-300 shadow-xl flex items-center justify-center 
            focus:outline-none focus:ring-4 focus:ring-offset-2 focus:ring-indigo-300 transform active:scale-95 ${loading ? 'opacity-60 cursor-not-allowed' : ''} ${className}`}
    >
        {loading ? (
            <ArrowPathIcon className="h-5 w-5 mr-2 animate-spin" />
        ) : (
            children
        )}
    </button>
);


// 1. Time Allocation Donut Chart (Simulated using Tailwind/CSS)
const TimeAllocationChart = () => {
    
    return (
        <div className="bg-white p-6 rounded-xl shadow-2xl border border-gray-100 h-full">
            <h3 className="text-xl font-poppins font-bold text-gray-800 mb-6 flex items-center">
                <ClockIcon className="h-6 w-6 mr-2 text-indigo-600"/>
                Work Time Allocation
            </h3>
            
            {/* FIX: Removed lg:flex-row and width classes. All elements now stack vertically (flex-col) 
                and use w-full, guaranteeing space for long labels.
            */}
            <div className="flex flex-col items-start justify-between gap-6">
                
                {/* Simulated Chart Area (Progress Bars) - Always w-full */}
                <div className="w-full space-y-4"> 
                    {MOCK_TIME_ALLOCATION.map((item, index) => (
                        <div key={index}>
                            <div className="flex justify-between items-center mb-1">
                                {/* Ensure the label can grow and wrap */}
                                <span className="text-sm font-roboto text-gray-700 break-words pr-2">{item.category}</span>
                                {/* Ensure the percentage is fixed right */}
                                <span className="text-sm font-poppins font-semibold text-gray-900 flex-shrink-0">{item.percentage}%</span>
                            </div>
                            <div className={`w-full h-3 ${item.color}/20 rounded-full overflow-hidden`}>
                                <div 
                                    className={`h-full ${item.color} rounded-full transition-all duration-1000 ease-out`} 
                                    style={{ width: `${item.percentage}%` }}
                                ></div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Legend/Summary - Always w-full, placed below the progress bars */}
                <div className="w-full p-4 rounded-xl bg-gray-50 border border-gray-200 shadow-inner space-y-3">
                    <p className="font-poppins font-semibold text-gray-800 border-b pb-2">Key Insights</p>
                    {MOCK_TIME_ALLOCATION.map((item, index) => (
                        <div key={index} className="flex items-start text-sm font-roboto text-gray-600">
                            <span className={`h-3 w-3 rounded-full mt-1 mr-2 ${item.color} shadow flex-shrink-0`}></span>
                            <span className="flex-grow">{item.category}:</span> 
                            <span className="font-bold ml-1 text-gray-900 flex-shrink-0">{item.percentage}%</span>
                        </div>
                    ))}
                    <p className="pt-2 text-xs text-gray-500 border-t mt-3">Idle time requires immediate policy review.</p>
                </div>
            </div>
        </div>
    );
};

// 2. Productivity Trend Bar Chart (Simulated)
const ProductivityTrendChart = () => {
    return (
        <div className="bg-white p-6 rounded-xl shadow-2xl border border-gray-100 h-full flex flex-col">
            <h3 className="text-xl font-poppins font-bold text-gray-800 mb-6 flex items-center">
                <RocketLaunchIcon className="h-6 w-6 mr-2 text-green-600"/>
                Productivity Trend (YoY)
            </h3>
            
            <div className="flex-grow flex items-end justify-around h-64 p-3 bg-gray-50 rounded-xl border border-gray-200 shadow-inner">
                {MOCK_PERFORMANCE_TRENDS.map((data, index) => (
                    <div key={index} className="flex flex-col items-center justify-end h-full w-1/6 group relative">
                        {/* Bar */}
                        <div 
                            className={`w-full bg-green-500 rounded-t-lg shadow-lg transition-all duration-700 ease-out hover:bg-green-600 transform hover:scale-y-105`}
                            style={{ height: `${data.productivity}%` }}
                        ></div>
                        {/* Label */}
                        <span className="mt-2 text-xs font-poppins font-medium text-gray-700">{data.month}</span>
                        {/* Tooltip/Hover Value */}
                        <span className="absolute bottom-full mb-2 text-xs font-bold px-2 py-1 bg-indigo-500 text-white rounded shadow-md opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap">
                            {data.productivity}%
                        </span>
                    </div>
                ))}
            </div>

            <p className="mt-4 text-sm text-gray-500 font-roboto border-t pt-4">4% average productivity growth over the last quarter.</p>
        </div>
    );
};

// 3. Agent Performance Table
const AgentPerformanceTable = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [isFiltering, setIsFiltering] = useState(false);
    
    // Combined mock data for a comprehensive view
    const MOCK_AGENT_DATA = [
        { id: 1, name: "Arjun Singh", productivity: 95, utilization: 98, avgSpeed: 30, violations: 1 },
        { id: 2, name: "Priya Sharma", productivity: 85, utilization: 90, avgSpeed: 45, violations: 5 },
        { id: 3, name: "Vikram Kumar", productivity: 70, utilization: 82, avgSpeed: 55, violations: 8 },
        { id: 4, name: "Ravi Menon", productivity: 60, utilization: 75, avgSpeed: 60, violations: 10 },
    ];

    const filteredAgents = useMemo(() => {
        if (!searchTerm) return MOCK_AGENT_DATA;
        
        // Simulate loading state for filter application
        if (isFiltering) return []; 
        
        return MOCK_AGENT_DATA.filter(agent => 
            agent.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [searchTerm, MOCK_AGENT_DATA, isFiltering]);

    const handleSearch = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        setIsFiltering(true);
        
        // Simulate filtering delay
        setTimeout(() => setIsFiltering(false), 500);
    };


    return (
        <div className="bg-white p-6 rounded-xl shadow-2xl border border-gray-100 h-full"> 
            <h3 className="text-xl font-poppins font-bold text-gray-800 mb-6 flex items-center">
                <UserGroupIcon className="h-6 w-6 mr-2 text-blue-600"/>
                Individual Agent Performance
            </h3>
            
            <div className="mb-4 flex items-center border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-blue-500 transition duration-150 shadow-inner">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 ml-3 flex-shrink-0" />
                <input
                    type="text"
                    placeholder="Search by Agent Name..."
                    value={searchTerm}
                    onChange={handleSearch}
                    className="w-full p-3 bg-transparent text-gray-700 font-roboto focus:outline-none"
                />
            </div>

            <div className="overflow-x-auto rounded-xl border border-gray-100 shadow-md">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-100/70">
                        <tr>
                            <th className="px-4 py-3 text-left text-xs font-poppins font-bold text-gray-700 uppercase tracking-wider">Agent</th>
                            <th className="px-4 py-3 text-center text-xs font-poppins font-bold text-gray-700 uppercase tracking-wider">Prod. (%)</th>
                            <th className="px-4 py-3 text-center text-xs font-poppins font-bold text-gray-700 uppercase tracking-wider">Util. (%)</th>
                            <th className="px-4 py-3 text-center text-xs font-poppins font-bold text-gray-700 uppercase tracking-wider">Violations</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100 font-roboto">
                        {isFiltering ? (
                            <tr><td colSpan="4" className="text-center py-6 text-indigo-500"><ArrowPathIcon className="h-6 w-6 mx-auto animate-spin" /> Filtering...</td></tr>
                        ) : filteredAgents.length > 0 ? (
                            filteredAgents.map((agent) => (
                                <tr key={agent.id} className="hover:bg-blue-50 transition duration-100"> 
                                    <td className="px-4 py-3 text-sm text-gray-900 font-semibold">{agent.name}</td>
                                    <td className="px-4 py-3 text-center whitespace-nowrap text-sm font-bold text-green-700">{agent.productivity}</td>
                                    <td className="px-4 py-3 text-center whitespace-nowrap text-sm text-gray-700">{agent.utilization}</td>
                                    <td className="px-4 py-3 text-center whitespace-nowrap text-sm font-bold text-red-700">{agent.violations}</td>
                                </tr>
                            ))
                        ) : (
                            <tr><td colSpan="4" className="text-center py-6 text-gray-500">No agents match your search criteria.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};


// --- MAIN DASHBOARD COMPONENT ---

export default function WorkforceAnalytics() {
    const [isRefreshing, setIsRefreshing] = useState(false);

    const handleRefresh = () => {
        setIsRefreshing(true);
        console.log("Refreshing analytics data...");
        // Simulate data refresh from API
        setTimeout(() => setIsRefreshing(false), 1000); 
    };

    return (
        <div 
            className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-10"
            style={{ fontFamily: "Poppins, Roboto, sans-serif" }}
        >
            {/* Page Header and Controls */}
            <div className="mb-8 border-b border-gray-200 pb-4 flex flex-col sm:flex-row justify-between items-start sm:items-center">
                <div className="mb-4 sm:mb-0">
                    <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 font-poppins flex items-center">
                        <ChartBarIcon className="h-8 w-8 mr-3 text-blue-600"/>
                        Workforce Analytics Hub
                    </h1>
                    <p className="text-gray-500 mt-1 text-md font-roboto">
                        Real-time productivity, utilization, and performance insights derived from field data.
                    </p>
                </div>
                <ActionButton 
                    onClick={handleRefresh}
                    className="bg-indigo-600 text-white hover:bg-indigo-700 active:bg-indigo-800 focus:ring-indigo-500 w-full sm:w-auto"
                    loading={isRefreshing}
                >
                    Refresh Data
                </ActionButton>
            </div>

            {/* 1. Key Metrics Summary */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
                <AnalyticsCard 
                    title="Average Productivity" 
                    value={`${MOCK_ANALYTICS_METRICS.averageProductivity}%`} 
                    icon={TrophyIcon} // Corrected: Using TrophyIcon
                    colorClass="text-indigo-600" 
                    footer="Verified work time vs. total shift hours"
                />
                <AnalyticsCard 
                    title="Average Utilization" 
                    value={`${MOCK_ANALYTICS_METRICS.averageUtilization}%`} 
                    icon={ClockIcon} 
                    colorClass="text-teal-600" 
                    footer="Active hours vs. scheduled hours"
                />
                <AnalyticsCard 
                    title="Top Performer" 
                    value={MOCK_ANALYTICS_METRICS.topPerformer} 
                    icon={RocketLaunchIcon} 
                    colorClass="text-green-600" 
                    footer="Highest compliance and productivity score"
                />
                <AnalyticsCard 
                    title="Lowest Performer" 
                    value={MOCK_ANALYTICS_METRICS.lowPerformer} 
                    icon={XCircleIcon} 
                    colorClass="text-red-600" 
                    footer="Requires coaching/review (Utilization < 78%)"
                />
            </div>

            {/* 2. Charts and Agent Detail (Current Layout: 2/3 width for Charts, 1/3 for Table) */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                
                {/* Combined Charts Container: Takes 2/3 of the screen width on large devices */}
                <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Time Allocation - Now guaranteed to fit by vertical stacking */}
                    <TimeAllocationChart />

                    {/* Performance Trend */}
                    <ProductivityTrendChart />
                </div>
                
                {/* Agent Detail Table (Remains 1/3 width) */}
                <div className="lg:col-span-1">
                    <AgentPerformanceTable />
                </div>
            </div>
            
            {/* 3. Footer */}
            <div className="mt-10 pt-4 border-t border-gray-200 text-center">
                <p className="text-sm text-gray-500 font-roboto">
                    Data last processed on {new Date().toLocaleDateString()}. Workforce analytics are based on policy adherence and geo-fenced time tracking.
                </p>
            </div>

        </div>
    );
}